import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import {
  Globe,
  Zap,
  Smartphone,
  Target,
  Shield,
  Clock,
  CheckCircle,
  Phone,
  Mail,
  ArrowRight,
  Users,
  FileText,
  BarChart3,
  MessageSquare,
  Calendar,
  Search,
  Layers,
  RefreshCw,
  PenTool,
  Settings,
  Star,
  ChevronRight,
  Monitor,
  Send,
  Building2,
  Award,
  Headphones,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebServicesPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    business_name: "",
    email: "",
    phone: "",
    industry: "",
    location: "",
    existing_website: "",
    goal: "",
    package_interest: "",
    timeline: "",
    notes: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.business_name,
        inquiry_type: "contracting",
        message: `WEB SERVICES INQUIRY\n\nBusiness: ${formData.business_name}\nIndustry: ${formData.industry}\nLocation: ${formData.location}\nExisting Website: ${formData.existing_website || "None"}\nGoal: ${formData.goal}\nPackage Interest: ${formData.package_interest}\nTimeline: ${formData.timeline}\n\nNotes: ${formData.notes}`,
      });
      toast.success("Mission received. We'll respond within 1 business day.");
      setFormData({
        name: "",
        business_name: "",
        email: "",
        phone: "",
        industry: "",
        location: "",
        existing_website: "",
        goal: "",
        package_interest: "",
        timeline: "",
        notes: "",
      });
    } catch (error) {
      toast.error("Submission failed. Please call or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  // Benefit pillars
  const benefits = [
    { icon: Globe, title: "Done-For-You", description: "Website + ongoing management handled completely" },
    { icon: Zap, title: "48-Hour Draft", description: "First draft delivered fast" },
    { icon: Smartphone, title: "Mobile + SEO Ready", description: "Built for phones and search engines" },
    { icon: Target, title: "Conversion-Focused", description: "Designed to get calls, quotes, bookings" },
    { icon: Shield, title: "Veteran-Led", description: "Mission-ready execution" },
    { icon: BarChart3, title: "Simple Pricing", description: "Transparent monthly plans" },
  ];

  // Services
  const services = [
    {
      icon: Monitor,
      title: "Business Websites",
      description: "Professional 3–10 page websites that establish credibility and convert visitors into customers.",
    },
    {
      icon: Target,
      title: "Landing Pages",
      description: "Single-page lead generation sites built for campaigns, ads, and specific offers.",
    },
    {
      icon: Shield,
      title: "Government Contractor Sites",
      description: "Capabilities, certifications, NAICS codes, and compliance-friendly layouts for federal positioning.",
    },
    {
      icon: PenTool,
      title: "Copywriting & Messaging",
      description: "Clear, compelling copy that speaks to your customers and drives action.",
    },
    {
      icon: RefreshCw,
      title: "Website Refresh / Rebuild",
      description: "Modernize your existing site with better design, faster performance, and stronger conversion.",
    },
    {
      icon: Search,
      title: "Basic SEO Setup",
      description: "Proper titles, meta descriptions, and structure so search engines can find you.",
    },
    {
      icon: MessageSquare,
      title: "Forms + Lead Routing",
      description: "Contact forms, quote requests, and booking forms with email delivery and CRM-ready setup.",
    },
    {
      icon: Settings,
      title: "Optional Add-Ons",
      description: "Blog, analytics dashboard, booking calendar, live chat, multi-location pages, bilingual support.",
    },
  ];

  // Pricing packages
  const packages = [
    {
      name: "Starter",
      setup: "$1,000",
      monthly: "$100",
      description: "Perfect for new businesses needing a professional online presence.",
      features: [
        "3–5 pages",
        "Mobile optimized",
        "Contact form",
        "Basic SEO setup",
        "Hosting included",
        "Monthly support",
      ],
      popular: false,
    },
    {
      name: "Professional",
      setup: "$2,500",
      monthly: "$150",
      description: "For businesses ready to grow with a conversion-focused website.",
      features: [
        "5–10 pages",
        "Strong conversion layout",
        "Quote/booking form",
        "On-page SEO + analytics",
        "2 rounds revisions",
        "Priority hosting",
        "Monthly support + edits",
      ],
      popular: true,
    },
    {
      name: "Gov / Premium",
      setup: "Starting at $5,000",
      monthly: "$250",
      description: "Built for government contractors and established businesses.",
      features: [
        "8–15 pages",
        "Contractor-focused structure",
        "Capability Statement page",
        "Certifications/NAICS pages",
        "Compliance-friendly layout",
        "Priority support",
        "Monthly edits included",
        "Analytics dashboard",
      ],
      popular: false,
    },
  ];

  // Process steps
  const processSteps = [
    { step: "01", title: "Quick Intake", description: "5-minute form tells us your business, goals, and timeline.", icon: FileText },
    { step: "02", title: "Draft Build", description: "First draft delivered within 48 hours.", icon: Zap },
    { step: "03", title: "Review + Revisions", description: "You review, we refine until it's right.", icon: RefreshCw },
    { step: "04", title: "Launch + Hosting", description: "We launch and handle all technical hosting.", icon: Globe },
    { step: "05", title: "Monthly Support", description: "Ongoing updates, security, backups, and edits.", icon: Headphones },
  ];

  // Portfolio placeholders
  const portfolio = [
    { title: "Logistics Company", industry: "Transportation & Logistics", description: "10-page website with quote request system and fleet showcase." },
    { title: "Security Contractor", industry: "Security Services", description: "Government-ready site with capabilities, certifications, and compliance layout." },
    { title: "Aviation Services", industry: "Aviation", description: "Professional site showcasing services with booking integration." },
    { title: "Local Service Business", industry: "Home Services", description: "5-page lead generation site with call tracking and contact forms." },
    { title: "Veteran-Owned Contractor", industry: "Government Contracting", description: "Capability statement, NAICS codes, and past performance pages." },
    { title: "Professional Services Firm", industry: "Consulting", description: "Modern site with team profiles, services, and scheduling integration." },
  ];

  // FAQ items
  const faqItems = [
    {
      question: "Who owns the domain?",
      answer: "You do. Your domain is registered in your name or your business name. We help with setup and management, but ownership stays with you.",
    },
    {
      question: "What's included in the monthly service?",
      answer: "Monthly service covers hosting, security updates, backups, SSL certificate, uptime monitoring, and reasonable support edits (text changes, image swaps, minor updates). Larger changes or new pages may be quoted separately.",
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes. We ask for 30 days notice. If you cancel, we can help transfer your site to another host or export your content. We want this to work for you—no hard feelings if needs change.",
    },
    {
      question: "How fast can you launch?",
      answer: "Most sites launch within 1–2 weeks after intake. First draft is typically delivered within 48 hours. Timeline depends on your feedback speed and revision rounds.",
    },
    {
      question: "Do you do logos and branding?",
      answer: "Basic logo assistance can be included for Starter and above. Full branding packages (logo design, color palettes, brand guides) are available as add-ons.",
    },
    {
      question: "Can you rebuild my current site?",
      answer: "Absolutely. Website refreshes and rebuilds are one of our core services. We'll modernize the design, improve performance, and strengthen conversion.",
    },
    {
      question: "Do you provide email accounts?",
      answer: "We can help set up professional email (you@yourbusiness.com) through Google Workspace or Microsoft 365. Email hosting is a separate subscription from those providers.",
    },
    {
      question: "Do you offer ongoing edits?",
      answer: "Yes. Monthly plans include reasonable support edits—text updates, image changes, and minor tweaks. Larger projects (new pages, redesigns) are quoted separately.",
    },
  ];

  // Testimonials (placeholders)
  const testimonials = [
    {
      quote: "They delivered exactly what we needed—fast, professional, and zero hassle. Our new site actually brings in leads now.",
      name: "Business Owner",
      company: "Local Service Company",
    },
    {
      quote: "Finally, a website that looks like we belong in federal contracting. The capability statement page alone was worth it.",
      name: "CEO",
      company: "Government Contractor",
    },
    {
      quote: "48-hour turnaround wasn't marketing—they actually did it. Site was live in a week.",
      name: "Operations Manager",
      company: "Logistics Company",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="web-services-page">
      {/* HERO SECTION */}
      <section className="relative py-20 md:py-28 bg-[#1a365d]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">
              AMR WEB COMMAND
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              WEBSITES THAT WORK.<br />
              <span className="text-[#c5a04a]">BUILT BY VETERANS.</span>
            </h1>
            <p className="text-xl text-slate-200 mb-4 leading-relaxed">
              Professional websites for small businesses, contractors, and veteran-owned companies. 
              We handle everything—design, hosting, updates—so you can focus on your business.
            </p>
            <p className="text-slate-300 mb-8">
              First draft in 48 hours. Simple monthly pricing. No tech headaches.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <a href="#get-quote">
                <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="hero-quote-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Get a Quote
                </Button>
              </a>
              <a href="tel:504-319-7683">
                <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="hero-call-btn">
                  <Phone className="w-4 h-4 mr-2" />
                  Book a 15-Min Call
                </Button>
              </a>
            </div>

            {/* Benefit Pills */}
            <div className="flex flex-wrap gap-3">
              {["Done-for-you", "48-hour draft", "Mobile + SEO ready", "Veteran-led"].map((item, index) => (
                <span key={index} className="px-4 py-2 bg-white/10 text-white text-xs font-heading tracking-wider rounded-sm">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFIT PILLARS */}
      <section className="py-16 bg-white border-b border-slate-200" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center" data-testid={`benefit-${index}`}>
                <div className="w-12 h-12 bg-[#1a365d] rounded-sm flex items-center justify-center mx-auto mb-3">
                  <benefit.icon className="w-6 h-6 text-[#c5a04a]" />
                </div>
                <h3 className="font-heading text-sm text-[#1a365d] mb-1">{benefit.title}</h3>
                <p className="text-slate-500 text-xs">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">WHAT WE BUILD</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">SERVICES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-tactical h-full" data-testid={`service-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#1a365d] rounded-sm flex items-center justify-center mb-4">
                    <service.icon className="w-6 h-6 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#1a365d] mb-2">{service.title}</h3>
                  <p className="text-slate-600 text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">INVESTMENT</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">SIMPLE PRICING</h2>
            <p className="text-slate-600 mt-4">Setup + monthly hosting/management. No surprises.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {packages.map((pkg, index) => (
              <Card 
                key={index} 
                className={`relative ${pkg.popular ? 'border-2 border-[#c5a04a] shadow-lg' : 'border border-slate-200'}`}
                data-testid={`package-${index}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#c5a04a] text-[#1a365d] text-xs font-heading tracking-wider px-4 py-1 rounded-sm">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="font-heading text-2xl text-[#1a365d]">{pkg.name}</CardTitle>
                  <CardDescription className="text-slate-600 text-sm">{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <p className="text-4xl font-heading text-[#1a365d]">{pkg.setup}</p>
                    <p className="text-slate-500 text-sm">setup</p>
                  </div>
                  <div className="mb-6 p-4 bg-slate-50 rounded-sm">
                    <p className="text-2xl font-heading text-[#c5a04a]">{pkg.monthly}</p>
                    <p className="text-slate-500 text-xs">per month (hosting + support)</p>
                  </div>
                  <ul className="space-y-3 text-left mb-6">
                    {pkg.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="#get-quote">
                    <Button 
                      className={`w-full ${pkg.popular ? 'btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332]' : 'btn-tactical btn-outline'} text-xs`}
                    >
                      Get Started
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm mt-8">
            Need something custom? We build custom scopes. <a href="#get-quote" className="text-[#c5a04a] hover:underline">Contact us</a>.
          </p>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 bg-[#1a365d]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">HOW IT WORKS</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white">OUR PROCESS</h2>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center relative" data-testid={`process-${index}`}>
                <div className="w-16 h-16 bg-[#c5a04a] rounded-sm flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-[#1a365d]" />
                </div>
                <span className="text-5xl font-heading text-white opacity-10 absolute top-0 right-0 md:right-4">
                  {step.step}
                </span>
                <h3 className="font-heading text-lg text-white mb-2">{step.title}</h3>
                <p className="text-slate-300 text-sm">{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 -right-3">
                    <ChevronRight className="w-6 h-6 text-[#c5a04a]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="py-20 bg-white" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">OUR WORK</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">PORTFOLIO</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolio.map((project, index) => (
              <Card key={index} className="card-tactical overflow-hidden" data-testid={`portfolio-${index}`}>
                <div className="h-48 bg-gradient-to-br from-[#1a365d] to-[#243b53] flex items-center justify-center">
                  <Globe className="w-16 h-16 text-[#c5a04a] opacity-50" />
                </div>
                <CardContent className="p-6">
                  <p className="text-[#c5a04a] text-xs font-heading tracking-wider mb-2">{project.industry}</p>
                  <h3 className="font-heading text-lg text-[#1a365d] mb-2">{project.title}</h3>
                  <p className="text-slate-600 text-sm">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">RESULTS</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">WHAT CLIENTS SAY</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="card-tactical" data-testid={`testimonial-${index}`}>
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-[#c5a04a] fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-700 italic mb-4">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-heading text-[#1a365d]">{testimonial.name}</p>
                    <p className="text-slate-500 text-sm">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">QUESTIONS</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">FAQ</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-50 border border-slate-200 rounded-sm px-6"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="text-left font-heading text-[#1a365d] hover:text-[#c5a04a] py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* GET A QUOTE FORM */}
      <section id="get-quote" className="py-20 bg-slate-50 border-t border-slate-200" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">START TODAY</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-4">GET A QUOTE</h2>
            <p className="text-slate-600">
              Tell us about your business. We'll respond within 1 business day.
            </p>
          </div>

          <Card className="card-tactical">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#1a365d] font-medium">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_name" className="text-[#1a365d] font-medium">Business Name *</Label>
                    <Input
                      id="business_name"
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      required
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      data-testid="input-business"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1a365d] font-medium">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#1a365d] font-medium">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="industry" className="text-[#1a365d] font-medium">Industry</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="e.g., Construction, Logistics, Security..."
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      data-testid="input-industry"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location" className="text-[#1a365d] font-medium">City / State</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., New Orleans, LA"
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      data-testid="input-location"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="existing_website" className="text-[#1a365d] font-medium">Existing Website (if any)</Label>
                  <Input
                    id="existing_website"
                    value={formData.existing_website}
                    onChange={(e) => setFormData({ ...formData, existing_website: e.target.value })}
                    placeholder="www.yourbusiness.com"
                    className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                    data-testid="input-website"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[#1a365d] font-medium">Goal *</Label>
                    <Select
                      value={formData.goal}
                      onValueChange={(value) => setFormData({ ...formData, goal: value })}
                      required
                    >
                      <SelectTrigger className="rounded-sm border-2 border-slate-200" data-testid="select-goal">
                        <SelectValue placeholder="Select goal" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="more-calls">More calls</SelectItem>
                        <SelectItem value="more-quotes">More quotes</SelectItem>
                        <SelectItem value="bookings">Bookings</SelectItem>
                        <SelectItem value="credibility">Credibility</SelectItem>
                        <SelectItem value="gov-contracting">Gov contracting</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1a365d] font-medium">Package Interest</Label>
                    <Select
                      value={formData.package_interest}
                      onValueChange={(value) => setFormData({ ...formData, package_interest: value })}
                    >
                      <SelectTrigger className="rounded-sm border-2 border-slate-200" data-testid="select-package">
                        <SelectValue placeholder="Select package" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">Starter ($1,000)</SelectItem>
                        <SelectItem value="professional">Professional ($2,500)</SelectItem>
                        <SelectItem value="gov-premium">Gov / Premium ($5,000+)</SelectItem>
                        <SelectItem value="not-sure">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1a365d] font-medium">Timeline</Label>
                    <Select
                      value={formData.timeline}
                      onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                    >
                      <SelectTrigger className="rounded-sm border-2 border-slate-200" data-testid="select-timeline">
                        <SelectValue placeholder="Select timeline" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asap">ASAP</SelectItem>
                        <SelectItem value="2-weeks">Within 2 weeks</SelectItem>
                        <SelectItem value="30-days">Within 30 days</SelectItem>
                        <SelectItem value="60-days">Within 60 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-[#1a365d] font-medium">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d] resize-none"
                    placeholder="Tell us more about your business, goals, or any specific requirements..."
                    data-testid="input-notes"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm"
                  data-testid="submit-btn"
                >
                  {loading ? "Submitting..." : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-slate-200 text-center">
                <p className="text-slate-600 mb-4">Prefer to talk now?</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href="tel:504-319-7683">
                    <Button variant="outline" className="btn-tactical btn-outline text-xs">
                      <Phone className="w-4 h-4 mr-2" />
                      Call 504-319-7683
                    </Button>
                  </a>
                  <a href="mailto:AMREID@AMRGLOBALHOLDINGS.COM">
                    <Button variant="outline" className="btn-tactical btn-outline text-xs">
                      <Mail className="w-4 h-4 mr-2" />
                      Email Us
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* START TODAY CTA */}
      <section className="py-20 bg-[#1a365d]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            READY FOR A WEBSITE THAT WORKS?
          </h2>
          <p className="text-slate-200 text-lg mb-8">
            Stop losing customers to a bad website. Get a professional site that brings in business—built by veterans who deliver.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a href="#get-quote">
              <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm">
                <FileText className="w-4 h-4 mr-2" />
                Get Your Quote
              </Button>
            </a>
            <a href="tel:504-319-7683">
              <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm">
                <Phone className="w-4 h-4 mr-2" />
                Book a Call
              </Button>
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-slate-400 text-sm">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#c5a04a]" />
              Veteran-led execution
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#c5a04a]" />
              Fast turnarounds
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#c5a04a]" />
              Transparent pricing
            </span>
          </div>
        </div>
      </section>

      {/* FOOTER TAGLINE */}
      <section className="py-8 bg-[#102a43]" data-testid="footer-tagline">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm mb-2">
            AMR Digital Infrastructure Division
          </p>
          <p className="text-white font-heading tracking-wider text-sm">
            AMR GLOBAL HOLDINGS LLC — VETERAN POWERED. MISSION READY.
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Veteran-Owned Small Business | New Orleans Lakefront Airport (KNEW)
          </p>
        </div>
      </section>
    </div>
  );
};

export default WebServicesPage;
